import { useEffect, useMemo, useState } from "react";

import {
  createParking,
  deleteParking,
  getParkingList,
  updateParking,
} from "@/apis/parking";
import type { ParkingInput, ParkingRecord } from "@/apis/parking";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import ParkingDetailModal from "@/pages/Admin/components/ParkingInfo/ParkingDetailModal";
import ParkingEditModal from "@/pages/Admin/components/ParkingInfo/ParkingEditModal";
import TableFilterButton from "@/pages/Admin/components/TableFilterButton";
import type { CellInfo } from "@/types/table";
import { getTableHeader } from "@/utils/table";

const PAGE_SIZE = 15;

type SearchType = "plate" | "phone" | "name";

const searchFilters: { label: string; type: SearchType }[] = [
  { label: "차량번호", type: "plate" },
  { label: "연락처", type: "phone" },
  { label: "이름", type: "name" },
];

const normalize = (s: string) => s.replace(/[\s\-/]/g, "").toLowerCase();

// 공용 Table 사용: 데스크톱은 표, 모바일(md 미만)은 카드 목록으로 렌더링된다
const parkingHeader = getTableHeader(
  ["이름", "연락처", "차종", "차량번호", ""],
  ["22%", "24%", "22%", "22%", "10%"],
);

const Badge = ({ items, color }: { items: string[]; color: string }) => {
  const colorMap: Record<string, string> = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    gray: "bg-gray-50 text-gray-600 border-gray-200",
  };
  const cls = colorMap[color] || colorMap.blue;

  return (
    <div className="flex flex-col gap-1">
      {items.map((item, i) => (
        <span
          key={i}
          className={`block w-fit whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const splitValues = (val: string) =>
  val
    .split("/")
    .map(s => s.trim())
    .filter(Boolean);

const ParkingInfo = () => {
  const [data, setData] = useState<ParkingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState<SearchType>("plate");
  const [searchValue, setSearchValue] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  // 상세 모달
  const [detailItem, setDetailItem] = useState<ParkingRecord | null>(null);

  // 수정/추가 모달
  const [editId, setEditId] = useState<number | null>(null);
  const [editRecord, setEditRecord] = useState<ParkingInput | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // 성공/에러 알림
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // 삭제 확인 대상
  const [deleteTarget, setDeleteTarget] = useState<ParkingRecord | null>(null);

  const fetchData = async () => {
    try {
      const res = await getParkingList();
      setData(res);
    } catch (e) {
      console.error("주차 목록 조회 실패:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const doSearch = () => {
    setQuery(searchValue);
    setPage(0);
  };
  const debouncedSearch = useDebouncedCallback(doSearch, 300);

  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = normalize(query);

    return data.filter(item => normalize(item[searchType]).includes(q));
  }, [query, searchType, data]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page],
  );

  const handleAdd = () => {
    setEditId(null);
    setEditRecord({
      name: "",
      phone: "",
      community: "",
      team: "",
      car: "",
      plate: "",
    });
    setIsAdding(true);
  };

  const handleEditFromDetail = () => {
    if (!detailItem) return;
    const { id, ...rest } = detailItem;
    handleCloseEdit();
    setDetailItem(null);
    setEditId(id);
    setEditRecord(rest);
    setIsAdding(false);
  };

  const handleSave = async (input: ParkingInput) => {
    const wasAdding = isAdding;
    try {
      if (isAdding) {
        const created = await createParking(input);
        setData(prev => [...prev, created]);
      } else if (editId !== null) {
        const updated = await updateParking(editId, input);
        setData(prev =>
          prev.map(item => (item.id === editId ? updated : item)),
        );
      }
      setAlertMessage(wasAdding ? "추가되었습니다." : "수정되었습니다.");
    } catch (e) {
      console.error("주차 저장 실패:", e);
      setAlertMessage(
        wasAdding ? "추가에 실패했습니다." : "수정에 실패했습니다.",
      );
    }
    setEditRecord(null);
    setEditId(null);
    setIsAdding(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    try {
      await deleteParking(id);
      setData(prev => prev.filter(item => item.id !== id));
      setAlertMessage("삭제되었습니다.");
    } catch (e) {
      console.error("주차 삭제 실패:", e);
      setAlertMessage("삭제에 실패했습니다.");
    }
    setDeleteTarget(null);
    setDetailItem(null);
  };

  const handleCloseEdit = () => {
    setEditRecord(null);
    setEditId(null);
    setIsAdding(false);
  };

  const placeholders: Record<SearchType, string> = {
    plate: "예: 12가 3456",
    phone: "예: 010-1234-5678",
    name: "예: 홍길동",
  };

  const tableBody: CellInfo[][] = paged.map(item => [
    {
      name: "이름",
      data: <Badge items={splitValues(item.name)} color="blue" />,
    },
    {
      name: "연락처",
      data: <Badge items={splitValues(item.phone)} color="green" />,
    },
    {
      name: "차종",
      data: <Badge items={splitValues(item.car)} color="gray" />,
    },
    {
      name: "차량번호",
      data: <Badge items={splitValues(item.plate)} color="purple" />,
    },
    { name: "", data: "상세", method: () => setDetailItem(item) },
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-gray-400">
        데이터 불러오는 중...
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 p-4 text-base text-black">
      {/* 검색 필터 */}
      <div className="flex items-center gap-2">
        {searchFilters.map(({ label, type }) => (
          <TableFilterButton
            key={type}
            isActive={searchType === type}
            onClick={() => {
              setSearchType(type);
              setPage(0);
            }}
          >
            {label}
          </TableFilterButton>
        ))}
      </div>

      {/* 검색바 + 추가 버튼 */}
      <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
        <div className="w-56">
          <input
            type="text"
            placeholder={placeholders[searchType]}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") debouncedSearch();
            }}
            className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          onClick={doSearch}
          className="h-10 rounded-lg border border-primary px-4 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white"
        >
          조회
        </button>
        <span className="ml-2 text-sm text-gray-400">
          총 {filtered.length}건
        </span>
        <div className="ml-auto">
          <button
            type="button"
            onClick={handleAdd}
            className="h-10 rounded-lg border border-primary bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            + 추가
          </button>
        </div>
      </div>

      {/* 테이블 (모바일에서는 카드 목록) */}
      {filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
          검색 결과가 없습니다.
        </div>
      ) : (
        <Table header={parkingHeader} body={tableBody} />
      )}

      {/* 페이징 */}
      {pageCount > 1 && (
        <div className="flex justify-center">
          <Pagination count={pageCount} page={page} onChange={setPage} />
        </div>
      )}

      {/* 수정/추가 모달 */}
      {editRecord && (
        <ParkingEditModal
          record={editRecord}
          isNew={isAdding}
          allData={data}
          editId={editId}
          onSave={handleSave}
          onClose={handleCloseEdit}
          onViewExisting={item => {
            setDetailItem(item);
          }}
        />
      )}

      {/* 상세 모달 (추가 모달 위에 렌더링) */}
      {detailItem && (
        <ParkingDetailModal
          record={detailItem}
          onEdit={handleEditFromDetail}
          onDelete={() => setDeleteTarget(detailItem)}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* 삭제 확인 */}
      {deleteTarget && (
        <ConfirmModal
          title="주차정보 삭제"
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        >
          정말 삭제하시겠습니까?
        </ConfirmModal>
      )}

      {/* 성공/에러 알림 (최상단) */}
      {alertMessage && (
        <AlertModal onClose={() => setAlertMessage(null)}>
          {alertMessage}
        </AlertModal>
      )}
    </div>
  );
};

export { Badge, splitValues };
export default ParkingInfo;
